/**
 * ActiveTicketCard - The user's active queue ticket card
 * (status badges, service details, large timers, estimated wait and actions)
 */

import { useTranslation } from 'react-i18next';
import { Bell, Trash2, Clock, AlertTriangle } from 'lucide-react';
import type { QueueTicket } from '../../../types';
import { formatTimeFromSeconds } from '../../../lib/timeUtils';
import { getStatusBadge, getStatusText } from './statusHelpers';

interface ActiveTicketCardProps {
  myTicket: QueueTicket;
  serviceTimeRemaining: number;
  timeRemaining: number;
  timeRemainingFormatted: string;
  estimatedWaitSeconds: number;
  isMonitoring: boolean;
  soundEnabled: boolean;
  marking: boolean;
  cancelling: boolean;
  removingTicket: boolean;
  onMarkArrival: () => void;
  onCancelTicket: () => void;
  onRemoveExpiredTicket: () => void;
}

// Format service time remaining (HH:MM:SS or MM:SS)
const formatServiceTimeRemaining = (seconds: number): string => {
  return formatTimeFromSeconds(seconds);
};

export function ActiveTicketCard({
  myTicket,
  serviceTimeRemaining,
  timeRemaining,
  timeRemainingFormatted,
  estimatedWaitSeconds,
  isMonitoring,
  soundEnabled,
  marking,
  cancelling,
  removingTicket,
  onMarkArrival,
  onCancelTicket,
  onRemoveExpiredTicket,
}: ActiveTicketCardProps) {
  const { t } = useTranslation();

  return (
    <div className="bg-surface border border-hairline rounded-xl p-6 mb-6 border-l-2 border-l-accent">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h2 className="font-sans text-xl font-semibold tracking-tight text-ink mb-2">
            Tu Turno: {myTicket.ticketNumber}
          </h2>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(myTicket.status)}`}>
              {getStatusText(myTicket.status, t)}
            </span>
            {isMonitoring && soundEnabled && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-surface-2 text-ink-muted rounded-full text-xs font-medium">
                <Bell className="w-3 h-3" aria-hidden="true" />
                Notificaciones activas
              </span>
            )}
          </div>
          {/* Service details */}
          {myTicket.serviceName && (
            <div className="mt-3 p-3 bg-surface-2 border border-hairline rounded-lg">
              <p className="text-xs text-ink-subtle uppercase tracking-wide">Servicio</p>
              <p className="font-semibold text-ink">{myTicket.serviceName}</p>
              {myTicket.servicePrice && (
                <p className="text-lg font-semibold text-ink tabular-nums mt-1">
                  {(myTicket.servicePrice / 100).toFixed(2)}€
                </p>
              )}
              {myTicket.barberName && (
                <p className="text-sm text-ink-muted mt-1">
                  Barbero: {myTicket.barberName}
                </p>
              )}
            </div>
          )}
        </div>
        <div className="text-right">
          <p className="text-xs text-ink-subtle uppercase tracking-wide">Posición</p>
          <p className="text-4xl font-semibold text-ink tabular-nums">{myTicket.position}</p>
        </div>
      </div>

      {/* LARGE Timer - Countdown */}
      {/* For in_service and finishing status, show calculated service time remaining */}
      {(myTicket.status === 'in_service' || myTicket.status === 'finishing') && serviceTimeRemaining > 0 ? (
        <div className="mb-6 p-6 bg-surface-2 rounded-xl border border-accent/40">
          <div className="text-center">
            <p className="inline-flex items-center gap-1.5 text-xs font-medium text-accent mb-3 tracking-wide uppercase">
              <Clock className="w-3.5 h-3.5" aria-hidden="true" /> Tiempo restante de servicio
            </p>
            <p className="text-6xl font-semibold text-accent tabular-nums">
              {formatServiceTimeRemaining(serviceTimeRemaining)}
            </p>
          </div>
        </div>
      ) : myTicket.timerExpiry && timeRemaining > 0 && (myTicket.status === 'waiting' || myTicket.status === 'notified') && myTicket.position === 1 ? (
        <div className="mb-6 p-6 bg-surface-2 rounded-xl border border-accent/40">
          <div className="text-center">
            <p className="inline-flex items-center gap-1.5 text-xs font-medium text-accent mb-3 tracking-wide uppercase">
              <Clock className="w-3.5 h-3.5" aria-hidden="true" />
              {myTicket.status === 'waiting' ? 'Tiempo para llegar' : 'Tiempo para presentarte'}
            </p>
            <p className="text-6xl font-semibold text-accent tabular-nums">
              {timeRemainingFormatted}
            </p>
            {timeRemaining < 300 && (
              <p className="inline-flex items-center gap-1.5 text-sm font-medium text-danger mt-3">
                <AlertTriangle className="w-4 h-4" aria-hidden="true" /> ¡Apúrate! El tiempo se está agotando
              </p>
            )}
          </div>
        </div>
      ) : null}

      {/* Estimated wait time */}
      {myTicket.status === 'waiting' && myTicket.position > 1 && estimatedWaitSeconds > 0 && (
        <div className="mb-4 p-6 bg-surface-2 rounded-xl border border-hairline">
          <div className="text-center">
            <p className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-subtle mb-2 tracking-wide uppercase">
              <Clock className="w-3.5 h-3.5" aria-hidden="true" /> Tiempo estimado de espera
            </p>
            <p className="text-5xl font-semibold text-ink tabular-nums">
              {formatTimeFromSeconds(estimatedWaitSeconds, { showHours: true })}
            </p>
            <p className="text-sm text-ink-muted mt-3">
              Aproximadamente {myTicket.position - 1} {myTicket.position - 1 === 1 ? 'persona' : 'personas'} adelante
            </p>

            {/* Alert when turn is approaching */}
            {myTicket.position <= 3 && myTicket.position > 1 && (
              <div className="mt-3 flex items-center justify-center gap-2 text-accent">
                <Bell className="w-4 h-4" aria-hidden="true" />
                <span className="text-sm font-medium">¡Tu turno se acerca!</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Actions — sticky para que Llegada/Cancelar queden siempre visibles sin scroll */}
      {myTicket.status === 'waiting' && (
        <div className="sticky bottom-[calc(4rem+env(safe-area-inset-bottom,0px)+0.75rem)] lg:bottom-3 z-50 space-y-3 bg-canvas border border-hairline rounded-xl p-3 -m-3 shadow-sm">
          <button
            onClick={onMarkArrival}
            disabled={marking}
            className="w-full bg-accent text-on-accent px-6 py-3 rounded-lg hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-focus"
          >
            {marking ? 'Marcando…' : 'Marcar Llegada a Sucursal'}
          </button>
          <button
            onClick={onCancelTicket}
            disabled={cancelling}
            className="w-full bg-transparent border border-hairline text-danger px-6 py-3 rounded-lg hover:bg-danger/20 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-colors"
          >
            {cancelling ? 'Cancelando…' : 'Cancelar Turno'}
          </button>
        </div>
      )}

      {myTicket.status === 'notified' && (
        <div className="p-4 bg-accent/10 border border-accent/40 rounded-lg text-center">
          <p className="font-sans text-xl font-bold text-accent mb-1">¡ES TU TURNO!</p>
          <p className="text-sm text-ink-muted">Preséntate en el mostrador ahora</p>
        </div>
      )}

      {myTicket.status === 'arrived' && (
        <div className="p-4 bg-surface-2 border border-hairline rounded-lg text-center">
          <p className="text-base font-semibold text-ink">Ya estás en la sucursal</p>
          <p className="text-sm text-ink-muted">Espera a que te llamen</p>
        </div>
      )}

      {myTicket.status === 'in_service' && (
        <div className="p-4 bg-surface-2 border border-hairline rounded-lg text-center">
          <p className="text-base font-semibold text-ink">Servicio en curso</p>
          <p className="text-sm text-ink-muted">Disfruta tu servicio</p>
        </div>
      )}

      {(myTicket.status === 'expired' || myTicket.status === 'cancelled') && (
        <div className="space-y-3">
          <div className="p-4 bg-danger/10 border border-danger/30 rounded-lg text-center">
            <p className="text-base font-semibold text-ink">
              {myTicket.status === 'expired' ? 'Turno Expirado' : 'Turno Cancelado'}
            </p>
            <p className="text-sm text-ink-muted">
              {myTicket.status === 'expired'
                ? 'Este turno ha expirado. Puedes eliminarlo de tu vista.'
                : 'Este turno fue cancelado. Puedes eliminarlo de tu vista.'}
            </p>
          </div>
          <button
            onClick={onRemoveExpiredTicket}
            disabled={removingTicket}
            className="w-full bg-surface-2 border border-hairline text-ink px-6 py-3 rounded-lg hover:bg-surface-3 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-colors flex items-center justify-center gap-2"
          >
            {removingTicket ? (
              <>
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Eliminando…
              </>
            ) : (
              <>
                <Trash2 className="w-5 h-5" aria-hidden="true" />
                Eliminar Turno
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
